import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, Check, AlertCircle } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/dnh/SiteChrome";
import { useAuth } from "../components/dnh/AuthContext";
import { safeReturnPath } from "../utils/safeReturnPath";

/* ============================================================
   DNH — Sign Up  (/signup · Phase A.6 prototype)
   ============================================================ */

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const PLAN_FREE = {
  id: "free",
  name: "Free",
  tagline: "Start reading with clarity",
  features: [
    "Daily free reading allowance",
    "Browse Highlights and topics",
    "Save stories and searches",
    "Optional weekly digest",
  ],
};

const PLAN_PRO = {
  id: "pro",
  name: "Premium",
  badge: "Most popular",
  tagline: "The full intelligence brief",
  yearly: { price: "12", period: "per year" },
  features: [
    "Unlimited signals",
    "Available country coverage",
    "All topics + narratives",
    "Narrative Perspectives",
    "Advanced search & filters",
    "Source-linked brief analysis",
  ],
  highlighted: true,
};

const BrandPanel = () => (
  <div className="relative h-full flex flex-col justify-between bg-[var(--ink)] px-10 py-12 xl:px-14 xl:py-14">
    <div
      className="absolute inset-0 opacity-[0.035]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(250,248,244,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(250,248,244,0.5) 1px, transparent 1px)",
        backgroundSize: "40px 40px",
      }}
      aria-hidden
    />
    <div className="absolute inset-y-0 left-0 w-[3px]" style={{ background: "var(--crimson)" }} aria-hidden />

    <div className="relative">
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[rgba(250,248,244,0.38)] mb-1.5">
        Daily News Highlights
      </div>
      <div className="font-serif text-[32px] leading-none text-[rgba(250,248,244,0.95)] tracking-[-0.015em]">
        DNH
      </div>
    </div>

    <div className="relative">
      <p className="font-serif text-[26px] sm:text-[28px] xl:text-[30px] leading-[1.18] text-[rgba(250,248,244,0.95)] tracking-[-0.012em]">
        "Start reading the world{" "}
        <span style={{ color: "rgba(166,25,46,0.9)" }}>differently."</span>
      </p>
      <p className="font-sans text-[14px] leading-[1.65] text-[rgba(250,248,244,0.48)] mt-5 max-w-[38ch]">
        Join readers who use DNH
        to understand what's actually happening — and why it matters.
      </p>
    </div>

    <div className="relative">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[rgba(250,248,244,0.35)] mb-3">
        What Premium unlocks
      </div>
      <ul className="space-y-2.5">
        {[
          "Unlimited available briefs",
          "Full narrative timelines (Past · Present · Predict)",
          "Four-lens Narrative Perspectives",
          "Advanced search with cross-context filters",
          "Source-linked context and analysis",
        ].map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <span
              className="mt-[5px] inline-block w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: "var(--crimson)" }}
            />
            <span className="font-sans text-[13px] text-[rgba(250,248,244,0.48)]">{f}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const PlanCard = ({ plan, selected, onSelect, price, period, save }) => (
  <button
    type="button"
    onClick={() => onSelect(plan.id)}
    data-testid={`signup-plan-${plan.id}`}
    className={`relative flex-1 rounded-[12px] border p-4 pt-7 text-left transition-all duration-200 ${
      selected
        ? plan.highlighted
          ? "border-[var(--crimson)] bg-[var(--crimson)]/5 shadow-[0_0_0_1px_var(--crimson)]"
          : "border-[var(--ink)] bg-[var(--surface-2)] shadow-[0_0_0_1px_var(--ink)]"
        : "border-[var(--hairline)] bg-[var(--paper)] hover:border-[var(--hairline-strong)]"
    }`}
  >
    {plan.badge && (
      <span
        className="absolute -top-2.5 left-3 font-mono text-[9.5px] tracking-[0.18em] uppercase px-2 py-[2px] rounded-full text-[var(--paper)]"
        style={{ background: "var(--crimson)" }}
      >
        {plan.badge}
      </span>
    )}

    {/* Selected tick — top-left interior, never overlapping price */}
    {selected && (
      <div
        className="absolute top-3 left-3 w-5 h-5 rounded-full flex items-center justify-center"
        style={{ background: plan.highlighted ? "var(--crimson)" : "var(--ink)" }}
        data-testid={`signup-plan-selected-${plan.id}`}
        aria-hidden
      >
        <Check size={11} strokeWidth={2.5} className="text-[var(--paper)]" />
      </div>
    )}

    <div className="flex items-start justify-between gap-3 mb-3">
      <div className={selected ? "pl-7" : ""}>
        <div className="font-serif text-[18px] leading-none text-[var(--ink)]">{plan.name}</div>
        <div className="font-mono text-[10px] tracking-[0.12em] uppercase text-[var(--muted)] mt-1">
          {plan.tagline}
        </div>
      </div>
      <div className="text-right shrink-0">
        <span className="font-serif text-[22px] leading-none text-[var(--ink)]">
          {price === "0" ? "Free" : `$${price}`}
        </span>
        {price !== "0" && (
          <div className="font-mono text-[9px] text-[var(--muted)] mt-0.5">{period}</div>
        )}
        {save && (
          <div className="font-mono text-[9px] tracking-[0.08em] uppercase text-[var(--crimson)] mt-1">
            {save}
          </div>
        )}
      </div>
    </div>

    <ul className="space-y-1.5">
      {plan.features.map((f) => (
        <li key={f} className="flex items-center gap-2">
          <Check
            size={12}
            strokeWidth={2.5}
            className={selected && plan.highlighted ? "text-[var(--crimson)]" : "text-[var(--muted)]"}
          />
          <span className="font-sans text-[12px] text-[var(--ink-2)]">{f}</span>
        </li>
      ))}
    </ul>
  </button>
);

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const handleGoogleSignUp = (from) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  window.location.href = `${backendUrl}/api/auth/google?from_path=${encodeURIComponent(safeReturnPath(from))}`;
};

export default function SignUp() {
  const [showPwd, setShowPwd] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("pro");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const target = params.has("from") ? safeReturnPath(params.get("from")) : selectedPlan === "pro" ? "/subscribe" : "/";
  const proPrice = PLAN_PRO.yearly;
  const proCtaPrice = `$${PLAN_PRO.yearly.price}/year`;

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim() || !password) {
      setError("Please fill in all fields.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: name.trim(), email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        setError(Array.isArray(detail) ? detail.map((d) => d.msg).join(" ") : (detail || "Registration failed"));
      } else {
        const signedIn = await checkAuth({ force: true });
        if(!signedIn) { setError("Your account was created, but the session could not be confirmed. Please sign in."); return; }
        navigate(target, { replace: true });
      }
    } catch {
      setError("Network error — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)]" data-testid="signup-page">
      <SiteHeader activeNav="" ticker />

      <main className="flex">
        {/* Left: Brand panel — desktop only */}
        <div className="hidden lg:block lg:w-[42%] xl:w-[38%] flex-shrink-0">
          <BrandPanel />
        </div>

        {/* Right: Form */}
        <div className="flex-1 flex flex-col overflow-y-auto">
          <div className="flex-1 flex items-start justify-center px-5 py-10 sm:px-10">
            <div className="w-full max-w-[460px]">
              {/* Heading */}
              <div className="mb-7">
                <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--muted)] mb-3">
                  Create account
                </div>
                <h1
                  className="font-serif text-[34px] sm:text-[40px] leading-[1.05] tracking-[-0.015em] text-[var(--ink)]"
                  data-testid="signup-title"
                >
                  Create your account.
                </h1>
                <p className="font-sans text-[14.5px] leading-[1.5] text-[var(--ink-2)] mt-2">
                  Start your intelligence brief in 60 seconds.
                </p>
              </div>

              {/* Form */}
              <form className="space-y-4" data-testid="signup-form" onSubmit={handleRegister}>
                {/* Name */}
                <div>
                  <label
                    htmlFor="signup-name"
                    className="block font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--muted)] mb-1.5"
                  >
                    Full name
                  </label>
                  <input
                    id="signup-name"
                    type="text"
                    autoComplete="name"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="signup-name-input"
                    className="w-full h-12 px-4 border border-[var(--hairline)] rounded-[10px] bg-[var(--paper)] font-sans text-[15px] text-[var(--ink)] placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--ink)] transition-colors"
                  />
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="signup-email"
                    className="block font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--muted)] mb-1.5"
                  >
                    Email
                  </label>
                  <input
                    id="signup-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="signup-email-input"
                    className="w-full h-12 px-4 border border-[var(--hairline)] rounded-[10px] bg-[var(--paper)] font-sans text-[15px] text-[var(--ink)] placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--ink)] transition-colors"
                  />
                </div>

                {/* Password */}
                <div>
                  <label
                    htmlFor="signup-password"
                    className="block font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--muted)] mb-1.5"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="signup-password"
                      type={showPwd ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="Min. 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="signup-password-input"
                      className="w-full h-12 px-4 pr-11 border border-[var(--hairline)] rounded-[10px] bg-[var(--paper)] font-sans text-[15px] text-[var(--ink)] placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--ink)] transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
                      aria-label={showPwd ? "Hide password" : "Show password"}
                      data-testid="signup-toggle-password"
                    >
                      {showPwd ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
                    </button>
                  </div>
                </div>

                {/* Plan picker */}
                <div>
                  <div className="flex items-center justify-between mb-2.5">
                    <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--muted)]">
                      Choose your plan
                    </div>
                    <span data-testid="signup-billing-yearly" className="font-mono text-xs text-[var(--muted)]">Annual billing</span>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3" data-testid="signup-plan-picker">
                    <PlanCard
                      plan={PLAN_FREE}
                      selected={selectedPlan === PLAN_FREE.id}
                      onSelect={setSelectedPlan}
                      price="0"
                      period="forever"
                    />
                    <PlanCard
                      plan={PLAN_PRO}
                      selected={selectedPlan === PLAN_PRO.id}
                      onSelect={setSelectedPlan}
                      price={proPrice.price}
                      period={proPrice.period}
                    />
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div
                    className="flex items-start gap-2.5 rounded-[8px] border border-[var(--crimson)]/30 bg-[var(--crimson)]/5 px-4 py-3"
                    data-testid="signup-error"
                  >
                    <AlertCircle size={15} strokeWidth={2} className="text-[var(--crimson)] shrink-0 mt-[1px]" />
                    <p className="font-sans text-[13px] text-[var(--crimson)] leading-[1.5]">{error}</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="signup-submit-button"
                  className="w-full h-12 rounded-full bg-[var(--ink)] text-[var(--paper)] font-sans text-[14px] font-medium flex items-center justify-center gap-2 hover:bg-[var(--crimson)] transition-colors mt-1 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Creating account…" : (selectedPlan === "pro" ? `Create account — ${proCtaPrice}` : "Create free account")}
                  {!loading && <ArrowRight size={15} strokeWidth={2} />}
                </button>

                <p className="font-mono text-[10px] tracking-[0.1em] text-center text-[var(--muted)]">
                  {selectedPlan === "pro"
                    ? "Annual subscription · No charge until checkout"
                    : "No credit card required · Upgrade anytime"}
                </p>
              </form>

              {/* Divider */}
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-[var(--hairline)]" />
                <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-[var(--muted)]">or</span>
                <div className="flex-1 h-px bg-[var(--hairline)]" />
              </div>

              {/* Google */}
              <button
                type="button"
                data-testid="signup-google-button"
                onClick={() => handleGoogleSignUp(target)}
                className="w-full h-12 rounded-full border border-[var(--hairline)] bg-[var(--paper)] font-sans text-[14px] text-[var(--ink)] flex items-center justify-center gap-3 hover:border-[var(--hairline-strong)] hover:bg-[var(--surface-2)] transition-colors"
              >
                <GoogleIcon />
                Continue with Google
              </button>

              {/* Sign in link */}
              <p className="mt-7 text-center font-sans text-[13.5px] text-[var(--muted)]">
                Already a member?{" "}
                <Link
                  to={`/signin?from=${encodeURIComponent(target)}`}
                  className="text-[var(--ink)] font-medium hover:text-[var(--crimson)] transition-colors"
                  data-testid="signup-signin-link"
                >
                  Sign in →
                </Link>
              </p>

              {/* Legal */}
              <p className="mt-5 text-center font-mono text-[10px] text-[var(--muted)] leading-[1.7]">
                By creating an account you agree to our{" "}
                <Link to="/about" className="underline hover:text-[var(--ink)]">Terms of Service</Link>{" "}
                and{" "}
                <Link to="/about" className="underline hover:text-[var(--ink)]">Privacy Policy</Link>.
              </p>
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
